import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, X } from "@phosphor-icons/react";
import PageHeader from "@/components/PageHeader";

export default function Risks() {
  const [items, setItems] = useState([]);
  const [f, setF] = useState({ title: "", category: "operational", probability: 5, impact: 5, mitigation: "" });
  const [showForm, setShowForm] = useState(false);

  const load = async () => setItems((await api.get("/risks")).data);
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    await api.post("/risks", f);
    toast.success("Risk logged");
    setShowForm(false);
    setF({ title: "", category: "operational", probability: 5, impact: 5, mitigation: "" });
    load();
  };

  const close = async (id) => {
    await api.patch(`/risks/${id}`, { status: "closed" });
    toast.success("Risk closed");
    load();
  };

  const cell = (p, i) => {
    const exp = p * i;
    const bg = exp >= 60 ? "bg-red-500/20 border-red-500/40" : exp >= 30 ? "bg-amber-500/20 border-amber-500/40" : "bg-emerald-500/10 border-emerald-500/30";
    return `flex items-center justify-center border ${bg} h-10 rounded-sm font-mono text-xs`;
  };

  const sorted = [...items].sort((a, b) => (b.exposure || 0) - (a.exposure || 0));

  return (
    <div className="p-8 max-w-[1400px] mx-auto">
      <PageHeader
        overline="Risk Register"
        title="Risks"
        subtitle="Probability × Impact heat map. Escalate before failure."
        actions={<button data-testid="new-risk-btn" onClick={() => setShowForm(true)} className="btn-primary text-sm flex items-center gap-1"><Plus size={14} /> New risk</button>}
      />

      {showForm && (
        <div className="oz-card p-5 mb-6">
          <form onSubmit={create} className="space-y-3">
            <input required placeholder="Title" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} className="w-full bg-[color:var(--oz-page)] border border-[color:var(--oz-border)] px-3 py-2 text-sm rounded-sm" />
            <div className="grid grid-cols-3 gap-3">
              <select value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} className="bg-[color:var(--oz-page)] border border-[color:var(--oz-border)] px-3 py-2 text-sm rounded-sm">
                {["operational", "financial", "reputational", "personnel", "strategic", "compliance"].map((c) => <option key={c}>{c}</option>)}
              </select>
              <label className="text-xs">Probability {f.probability}
                <input type="range" min="1" max="10" value={f.probability} onChange={(e) => setF({ ...f, probability: +e.target.value })} className="w-full" />
              </label>
              <label className="text-xs">Impact {f.impact}
                <input type="range" min="1" max="10" value={f.impact} onChange={(e) => setF({ ...f, impact: +e.target.value })} className="w-full" />
              </label>
            </div>
            <textarea placeholder="Mitigation plan" value={f.mitigation} onChange={(e) => setF({ ...f, mitigation: e.target.value })} rows="2" className="w-full bg-[color:var(--oz-page)] border border-[color:var(--oz-border)] px-3 py-2 text-sm rounded-sm" />
            <div className="flex gap-2">
              <button className="btn-primary text-sm">Save</button>
              <button type="button" onClick={() => setShowForm(false)} className="btn-ghost text-sm">Cancel</button>
            </div>
          </form>
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 oz-card overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[color:var(--oz-border)]">
                <th className="text-left px-4 py-2 overline">Exposure</th>
                <th className="text-left px-4 py-2 overline">Level</th>
                <th className="text-left px-4 py-2 overline">Risk</th>
                <th className="text-left px-4 py-2 overline">P × I</th>
                <th className="text-left px-4 py-2 overline">Category</th>
                <th className="text-left px-4 py-2 overline"></th>
              </tr>
            </thead>
            <tbody>
              {sorted.length === 0 && <tr><td colSpan="6" className="p-8 text-center text-[color:var(--oz-text-muted)]">No risks logged.</td></tr>}
              {sorted.map((r) => (
                <tr key={r.id} className="oz-row" data-testid={`risk-${r.id}`}>
                  <td className="px-4 py-2.5 font-mono">{r.exposure}</td>
                  <td className="px-4 py-2.5"><span className={`chip ${r.level === "high" ? "chip-red" : r.level === "medium" ? "chip-yellow" : "chip-green"}`}>{r.level?.toUpperCase()}</span></td>
                  <td className="px-4 py-2.5 max-w-md">
                    <div className="text-sm">{r.title}</div>
                    <div className="text-xs text-[color:var(--oz-text-muted)] mt-0.5">{r.mitigation}</div>
                  </td>
                  <td className="px-4 py-2.5 font-mono text-xs">{r.probability}×{r.impact}</td>
                  <td className="px-4 py-2.5 text-xs text-[color:var(--oz-text-dim)]">{r.category}</td>
                  <td className="px-4 py-2.5 text-right">
                    {r.status === "open" && <button onClick={() => close(r.id)} className="text-xs text-[color:var(--oz-text-muted)] hover:text-[color:var(--oz-emerald)]">Close</button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="oz-card p-5">
          <div className="overline mb-3">Heat Map</div>
          <div className="text-[10px] font-mono text-[color:var(--oz-text-muted)] mb-2 text-center">Probability →</div>
          <div className="grid grid-cols-6 gap-1">
            <div></div>
            {[2, 4, 6, 8, 10].map((p) => <div key={p} className="text-center text-[10px] font-mono text-[color:var(--oz-text-muted)]">{p}</div>)}
            {[10, 8, 6, 4, 2].map((i) => (
              <React.Fragment key={i}>
                <div className="text-right text-[10px] font-mono text-[color:var(--oz-text-muted)] pr-1 flex items-center justify-end">{i}</div>
                {[2, 4, 6, 8, 10].map((p) => {
                  const risksHere = items.filter((r) => Math.abs(r.probability - p) <= 1 && Math.abs(r.impact - i) <= 1).length;
                  return <div key={p} className={cell(p, i)}>{risksHere > 0 ? risksHere : ""}</div>;
                })}
              </React.Fragment>
            ))}
          </div>
          <div className="text-[10px] font-mono text-[color:var(--oz-text-muted)] mt-2 -rotate-90 origin-left absolute" style={{ display: "none" }}>Impact</div>
        </div>
      </div>
    </div>
  );
}
